import type { TextStyle, ViewStyle } from 'react-native';

import { colors } from '@packages/core/styles';
import { AlertBar, Button, Icon, Typography } from '@packages/ui/shared';
import type { CvtConcernDoc } from '@web/vin-sessions/types';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { CvtConcernDocList } from './cvt-concern-doc-list';
import { CvtConcernDocListItem } from './cvt-concern-doc-list-item';
import { CvtConcern, type CvtConcernOption } from './cvt-primary-concerns-modal.types';

const POLICY_AND_PROCEDURE_URL =
    'https://www.subarunet.com/snet/secure/_content/service/new_claims_policies_manual/html5_version/Section_05/5.2_automotive_repairs.htm#5.2.17_Transmissions';

const alertBarStyle: ViewStyle = { marginBottom: 12 };
const typographyStyle: TextStyle = { lineHeight: 24 };
const popupTipStyle: TextStyle = { fontSize: 14, fontStyle: 'italic', fontWeight: '600', color: '#B45309' };

const useStyles = createUseStyles({
    anchor: {
        color: colors.blueLink,
        textDecoration: 'none',
        '&:hover': {
            color: colors.blueLinkHover,
        },
    },
});

export type CvtDiagnosticNotRequiredProps = {
    /** The CVT concern documents to list. */
    cvtConcernDocs: CvtConcernDoc[];

    /** Whether the CVT concern documents failed to load. */
    hasFailedToLoadCvtConcernDocs: boolean;

    /** Whether the CVT concern documents are still loading. */
    isLoadingCvtConcernDocs: boolean;

    /** Takes the user back to select their concerns. */
    onBack: () => void;

    /** Opens the CVT concern document at the given URL in a new tab. */
    onDocumentOpen: (preSignedUrl: string) => void;

    /** Navigates the user to the STIS page. */
    onGoToStis: () => void;

    /** Closes the alert bar that notifies the user the browser blocked a new tab. */
    onPopupBlockedAlertClose: () => void;

    /** The selected concerns, which never include drivability. */
    selectedConcernOptions: CvtConcernOption[];

    /** Whether to show the alert bar that notifies the user the browser blocked a new tab. */
    showPopupBlockedAlert: boolean;
};

/**
 * The CVT primary concerns modal's content for concerns that don't need the CVT application.
 *
 * Renders why, for a transmission DTC, a noise or fluid leak, or both, with the TSBs or policy to review instead, and
 * buttons back to the concerns or on to STIS.
 */
export function CvtDiagnosticNotRequired({
    cvtConcernDocs,
    hasFailedToLoadCvtConcernDocs,
    isLoadingCvtConcernDocs,
    onBack,
    onDocumentOpen,
    onGoToStis,
    onPopupBlockedAlertClose,
    selectedConcernOptions,
    showPopupBlockedAlert,
}: CvtDiagnosticNotRequiredProps) {
    const { t } = useTranslation();
    const classes = useStyles();

    const hasDtcConcern = selectedConcernOptions.some((option) => option.value === CvtConcern.Dtc);
    const hasNoiseOrLeakConcern = selectedConcernOptions.some((option) => option.value === CvtConcern.NoiseOrLeak);

    return (
        <>
            <Modal.Header closeButton>
                <Modal.Title>{t('cvt:notRequiredModal.title', 'CVT application not required')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <AlertBar info onClose={onPopupBlockedAlertClose} show={showPopupBlockedAlert} style={alertBarStyle}>
                    <div className="d-flex flex-column gap-2">
                        <Typography variant="label">
                            {t('cvt:notRequiredModal.popupBlockedAlert.title', 'Your browser blocked the new tab.')}
                        </Typography>
                        <Typography>
                            {t(
                                'cvt:notRequiredModal.popupBlockedAlert.description',
                                'To fix this, allow pop-ups for TechShare in your browser settings.'
                            )}
                        </Typography>
                        <Typography style={popupTipStyle}>
                            {t(
                                'cvt:notRequiredModal.popupBlockedAlert.popupTip',
                                'Tip: when a pop-up is blocked, a small "Pop-up blocked" icon appears at the right end of the address bar — click it and choose "Always allow" to fix it right away.'
                            )}
                        </Typography>
                    </div>
                </AlertBar>

                {hasDtcConcern && !hasNoiseOrLeakConcern && (
                    <div className="d-flex flex-column gap-4">
                        <Typography variant="label" style={typographyStyle}>
                            {t(
                                'cvt:notRequiredModal.dtc.reason',
                                'A transmission DTC alone does not require the use of the CVT diagnostic assistance application.'
                            )}
                        </Typography>
                        <Typography style={typographyStyle}>
                            {t(
                                'cvt:notRequiredModal.dtc.nextSteps',
                                'Begin with searching the DTCs in STIS and review all applicable TSBs and service information. The following TSBs may contain relevant diagnostic procedures, reprogramming files, or additional guidance:'
                            )}
                        </Typography>
                        <CvtConcernDocList
                            cvtConcernDocs={cvtConcernDocs}
                            hasFailedToLoadCvtConcernDocs={hasFailedToLoadCvtConcernDocs}
                            isLoadingCvtConcernDocs={isLoadingCvtConcernDocs}
                            onDocumentOpen={onDocumentOpen}
                        />
                        <Typography color="textDarkSecondary" style={typographyStyle}>
                            {t(
                                'cvt:notRequiredModal.dtc.note',
                                'CVT replacement requests may require an NQR with adequate supporting evidence for warranty authorization.'
                            )}
                        </Typography>
                    </div>
                )}

                {hasNoiseOrLeakConcern && !hasDtcConcern && (
                    <div className="d-flex flex-column gap-4">
                        <Typography variant="label" style={typographyStyle}>
                            {t(
                                'cvt:notRequiredModal.noiseOrLeak.reason',
                                'Transmission noise or fluid leak concerns alone do not require the use of the CVT diagnostic application.'
                            )}
                        </Typography>
                        <Typography style={typographyStyle}>
                            {t(
                                'cvt:notRequiredModal.noiseOrLeak.nextSteps',
                                'Begin with searching STIS and review all applicable TSBs and service information. CVT replacement should only be considered after all required component-level repairs have been performed and the customer concern remains unresolved.'
                            )}
                        </Typography>
                        <ul className="mb-0">
                            <CvtConcernDocListItem>
                                <Typography style={typographyStyle}>
                                    <a
                                        className={classes.anchor}
                                        href={POLICY_AND_PROCEDURE_URL}
                                        rel="noopener noreferrer"
                                        target="_blank"
                                    >
                                        {t(
                                            'cvt:notRequiredModal.documents.policies.componentLevelRepairs',
                                            'Policy & Procedure 5.2 — component-level repairs'
                                        )}
                                    </a>
                                </Typography>
                            </CvtConcernDocListItem>
                        </ul>
                        <Typography color="textDarkSecondary" style={typographyStyle}>
                            {t(
                                'cvt:notRequiredModal.noiseOrLeak.note',
                                'If diagnosis results in a CVT replacement recommendation, warranty authorization may require a Non-Quality Report (NQR) with sufficient supporting evidence.'
                            )}
                        </Typography>
                    </div>
                )}

                {hasDtcConcern && hasNoiseOrLeakConcern && (
                    <div className="d-flex flex-column gap-4">
                        <Typography variant="label" style={typographyStyle}>
                            {t(
                                'cvt:notRequiredModal.dtcAndNoiseOrLeak.reason',
                                'Transmission DTCs, noise concerns, or fluid leaks alone do not require the use of the CVT diagnostic application.'
                            )}
                        </Typography>
                        <Typography style={typographyStyle}>
                            {t(
                                'cvt:notRequiredModal.dtcAndNoiseOrLeak.nextSteps',
                                'Begin with searching STIS and review all applicable TSBs and service information.'
                            )}
                        </Typography>
                        <div className="d-flex flex-column gap-4 mt-2">
                            <Typography style={typographyStyle}>
                                {t(
                                    'cvt:notRequiredModal.dtcAndNoiseOrLeak.tsbListIntro',
                                    'The following TSBs may contain diagnostic procedures, reprogramming files, or additional guidance:'
                                )}
                            </Typography>
                            <CvtConcernDocList
                                cvtConcernDocs={cvtConcernDocs}
                                hasFailedToLoadCvtConcernDocs={hasFailedToLoadCvtConcernDocs}
                                isLoadingCvtConcernDocs={isLoadingCvtConcernDocs}
                                onDocumentOpen={onDocumentOpen}
                            />
                        </div>
                        <Typography style={typographyStyle}>
                            {t(
                                'cvt:notRequiredModal.dtcAndNoiseOrLeak.policyListIntro',
                                'For noise or fluid leak concerns, identify the source and perform all required component-level repairs before considering CVT replacement:'
                            )}
                        </Typography>
                        <ul className="mb-0">
                            <CvtConcernDocListItem>
                                <Typography style={typographyStyle}>
                                    <a
                                        className={classes.anchor}
                                        href={POLICY_AND_PROCEDURE_URL}
                                        rel="noopener noreferrer"
                                        target="_blank"
                                    >
                                        {t(
                                            'cvt:notRequiredModal.documents.policies.componentLevelRepairs',
                                            'Policy & Procedure 5.2 — component-level repairs'
                                        )}
                                    </a>
                                </Typography>
                            </CvtConcernDocListItem>
                        </ul>
                        <Typography color="textDarkSecondary" style={typographyStyle}>
                            {t(
                                'cvt:notRequiredModal.dtcAndNoiseOrLeak.note',
                                'CVT replacement requests may require a Non-Quality Report (NQR) with sufficient supporting evidence for warranty authorization.'
                            )}
                        </Typography>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <div className="d-flex justify-content-between w-100">
                    <Button
                        iconLeft={<Icon color="blueOne" name="chevron-left" size={18} />}
                        onPress={onBack}
                        variant="ghost-blue"
                    >
                        {t('buttons:back', 'Back')}
                    </Button>

                    <Button onPress={onGoToStis} variant="primary">
                        {t('cvt:notRequiredModal.buttons.goToStis', 'Go to STIS')}
                    </Button>
                </div>
            </Modal.Footer>
        </>
    );
}
